import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, API } from "@/lib/api";

// Neutral defaults only. Never use demo/Emergent/Unsplash media as initial state.
const DEFAULT = {
  logo: { light: "", dark: "" },
  home: {
    collections_kicker: "The Edit",
    collections_title: "The Collections",
    collections_desc: "Ten silhouettes, one atelier. Each collection is composed like an editorial — hand-embroidered, hand-finished, one piece at a time.",
    editorial: {
      kicker: "Featured Collection",
      title_line1: "Explore The", title_line2: "Collection",
      description: "Discover handcrafted couture designed for weddings, festive celebrations and unforgettable occasions.",
      image: "",
      cta_text: "Discover Bridal", cta_link: "/collections/bridal",
      cta2_text: "All Collections", cta2_link: "/collections",
      stats: [
        { value: "350+", label: "Hours per piece" },
        { value: "42", label: "Master karigars" },
        { value: "1of1", label: "Made to order" },
      ],
    },
    bridal: {
      kicker: "Bridal Atelier",
      title_line1: "Book A Private", title_line2: "Consultation",
      description: "Meet our couturiers at our New Delhi atelier for a private, unhurried fitting.",
      image: "",
    },
    new_arrivals_kicker: "Just Arrived",
    new_arrivals_title: "New Arrivals",
    sections: { editorial: true, new_arrivals: true, bridal: true, testimonials: true, instagram: true },
  },
  auth: { image: "", tagline: "Welcome back to the House of Barique." },
};

const DEFAULT_NAV = [
  { label: "Home", kind: "url", value: "/" },
  { label: "Collections", kind: "url", value: "/collections" },
  { label: "Women", kind: "url", value: "/collections/women" },
  { label: "Menswear", kind: "url", value: "/collections/menswear" },
  { label: "Bridal", kind: "url", value: "/collections/bridal" },
  { label: "Festive", kind: "url", value: "/collections/festive" },
  { label: "Occasion Wear", kind: "url", value: "/collections/cocktail" },
  { label: "New Arrivals", kind: "url", value: "/collections/new" },
  { label: "About", kind: "url", value: "/about" },
];

const abs = (url) => (!url ? url : url.startsWith("http") ? url : url.startsWith("/api/") ? API.replace(/\/api$/, "") + url : url);

const Ctx = createContext(null);
export const useSite = () => useContext(Ctx);

export const SiteProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT);
  const [nav, setNav] = useState(DEFAULT_NAV);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, n] = await Promise.all([
        api.get("/settings").catch(() => ({ data: null })),
        api.get("/nav").catch(() => ({ data: [] })),
      ]);

      if (s.data && s.data.home) {
        setSettings({
          ...DEFAULT,
          ...s.data,
          logo: { ...DEFAULT.logo, ...(s.data.logo || {}), light: abs(s.data.logo?.light) || "", dark: abs(s.data.logo?.dark) || "" },
          home: {
            ...DEFAULT.home,
            ...s.data.home,
            editorial: { ...DEFAULT.home.editorial, ...(s.data.home.editorial || {}), image: abs(s.data.home.editorial?.image) || "" },
            bridal: { ...DEFAULT.home.bridal, ...(s.data.home.bridal || {}), image: abs(s.data.home.bridal?.image) || "" },
          },
          auth: { ...DEFAULT.auth, ...(s.data.auth || {}), image: abs(s.data.auth?.image) || "" },
        });
      }
      if (Array.isArray(n.data)) setNav(n.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const navResolved = useMemo(() => nav.map(n => ({
    ...n,
    to: n.kind === "category" ? `/category/${n.value}` : n.value,
  })), [nav]);

  const value = useMemo(() => ({ settings, loading, nav: navResolved, rawNav: nav, refresh }), [settings, loading, nav, navResolved, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
